/**
 * 절차적 샘플 이미지.
 *
 * 빈 화면으로 시작하는 도구는 아무도 두 번 열지 않는다. 파일을 찾아오기 전에
 * 뭐가 나오는지 보여줘야 한다. 이미지를 저장소에 넣는 대신 코드로 그린다 —
 * 레포가 가벼워지고, 라이선스 걱정도 없다.
 */

/** 정수 해시 기반 값 노이즈. 시드가 같으면 결과도 같다. */
function makeNoise(seed: number) {
  const hash = (x: number, y: number) => {
    let h = (x * 374761393 + y * 668265263 + seed * 2246822519) | 0;
    h = (h ^ (h >>> 13)) * 1274126177;
    return ((h ^ (h >>> 16)) >>> 0) / 4294967295;
  };
  const smooth = (t: number) => t * t * (3 - 2 * t);
  const value = (x: number, y: number) => {
    const xi = Math.floor(x), yi = Math.floor(y);
    const tx = smooth(x - xi), ty = smooth(y - yi);
    const a = hash(xi, yi), b = hash(xi + 1, yi);
    const c = hash(xi, yi + 1), d = hash(xi + 1, yi + 1);
    return (a + (b - a) * tx) * (1 - ty) + (c + (d - c) * tx) * ty;
  };
  return (x: number, y: number, octaves = 5) => {
    let sum = 0, amp = 0.5, freq = 1, norm = 0;
    for (let o = 0; o < octaves; o++) {
      sum += value(x * freq, y * freq) * amp;
      norm += amp;
      amp *= 0.5;
      freq *= 2.07;
    }
    return sum / norm;
  };
}

interface SampleSpec {
  id: string;
  label: string;
  seed: number;
  /** 실루엣 반지름 변조 — 각도에 따라 울퉁불퉁하게. */
  lumps: number;
  lumpAmount: number;
  base: [number, number, number];
  accent: [number, number, number];
  grain: number;
  gloss: number;
}

export const SAMPLE_SPECS: SampleSpec[] = [
  {
    id: 'slime',
    label: '슬라임',
    seed: 7,
    lumps: 5,
    lumpAmount: 0.055,
    base: [58, 200, 172],
    accent: [180, 255, 236],
    grain: 0.12,
    gloss: 0.85,
  },
  {
    id: 'golem',
    label: '암석 골렘',
    seed: 23,
    lumps: 9,
    lumpAmount: 0.13,
    base: [120, 108, 96],
    accent: [206, 178, 132],
    grain: 0.55,
    gloss: 0.12,
  },
];

const SIZE = 512;

function renderSample(spec: SampleSpec): ImageData {
  const noise = makeNoise(spec.seed);
  const img = new ImageData(SIZE, SIZE);
  const cx = SIZE / 2;
  const cy = SIZE * 0.54;
  const baseRadius = SIZE * 0.36;

  // 광원 방향(정면 위 왼쪽). 셰이딩과 하이라이트가 여기서 나온다.
  const lx = -0.45, ly = -0.62, lz = 0.64;

  for (let y = 0; y < SIZE; y++) {
    for (let x = 0; x < SIZE; x++) {
      const p = (y * SIZE + x) * 4;
      const dx = (x - cx) / baseRadius;
      const dy = (y - cy) / baseRadius;
      const angle = Math.atan2(dy, dx);
      const dist = Math.hypot(dx, dy);

      // 각도에 따라 반지름을 흔들어 유기적인 실루엣을 만든다.
      const wobble =
        1 +
        Math.sin(angle * spec.lumps + spec.seed) * spec.lumpAmount +
        (noise(Math.cos(angle) * 2 + 5, Math.sin(angle) * 2 + 5, 3) - 0.5) * spec.lumpAmount * 2.4;
      const edge = dist / wobble;

      if (edge > 1) { img.data[p + 3] = 0; continue; }

      // 구 표면 법선을 가정한 셰이딩. 진짜 3D가 아니라 "3D처럼 찍힌 그림"을 만드는 것.
      const nz = Math.sqrt(Math.max(0, 1 - edge * edge));
      const nx = dx / wobble;
      const ny = dy / wobble;
      const diffuse = Math.max(0, nx * lx + ny * ly + nz * lz);

      const grain = noise(x * 0.045, y * 0.045, 6);
      const fine = noise(x * 0.28 + 100, y * 0.28 + 100, 3);
      const bumps = (grain - 0.5) * spec.grain + (fine - 0.5) * spec.grain * 0.45;

      const shade = 0.22 + diffuse * 0.85 + bumps;
      const t = Math.min(1, Math.max(0, grain * 0.8 + diffuse * 0.35));

      // 스페큘러 — 이 하이라이트가 있어야 러프니스 추정이 재미있어진다.
      const half = [lx, ly - 0.15, lz + 0.9];
      const hlen = Math.hypot(half[0]!, half[1]!, half[2]!);
      const ndoth = Math.max(0, (nx * half[0]! + ny * half[1]! + nz * half[2]!) / hlen);
      const spec2 = Math.pow(ndoth, 24) * spec.gloss * (0.6 + fine * 0.6);

      // 실루엣 안쪽 가장자리를 어둡게 — 부피감을 준다.
      const rim = Math.pow(edge, 6) * 0.35;

      for (let c = 0; c < 3; c++) {
        const col = spec.base[c]! * (1 - t) + spec.accent[c]! * t;
        img.data[p + c] = col * shade * (1 - rim) + 255 * spec2;
      }
      img.data[p + 3] = 255 * Math.min(1, (1 - edge) * 14);
    }
  }
  return img;
}

// ---------------------------------------------------------------------------
// 갤러리 — 실제 생성형 이미지로 만든 테스트 스프라이트.
// 절차적 샘플은 "코드가 도는가"를 보여주고, 갤러리는 "결과가 쓸 만한가"를 보여준다.
// 각 항목은 프리셋 하나를 겨냥해 골랐다: 슬라임은 반사, 골렘은 요철, 기사는 금속…
// ---------------------------------------------------------------------------

import slimeUrl from '../assets/slime.webp';
import golemUrl from '../assets/golem.webp';
import knightUrl from '../assets/knight.webp';
import crystalUrl from '../assets/crystal.webp';
import foxUrl from '../assets/fire-fox.webp';
import treantUrl from '../assets/treant.webp';
import robotUrl from '../assets/robot.webp';
import beastUrl from '../assets/fluffy-beast.webp';
import wraithUrl from '../assets/ice-wraith.webp';
import dragonUrl from '../assets/dragon-whelp.webp';

export interface GalleryItem {
  id: string;
  label: string;
  url: string;
  /** 이 이미지가 잘 보여주는 프리셋. 힌트일 뿐 자동 선택은 통계가 한다. */
  showcases: string;
}

export const GALLERY: GalleryItem[] = [
  { id: 'slime', label: '슬라임', url: slimeUrl, showcases: '반사·이리데슨스' },
  { id: 'golem', label: '용암 골렘', url: golemUrl, showcases: '요철·발광' },
  { id: 'knight', label: '강철 기사', url: knightUrl, showcases: '메탈니스' },
  { id: 'crystal', label: '크리스탈', url: crystalUrl, showcases: '면·림라이트' },
  { id: 'fire-fox', label: '화염 여우', url: foxUrl, showcases: '발광' },
  { id: 'treant', label: '트렌트', url: treantUrl, showcases: '거친 러프니스' },
  { id: 'robot', label: '로봇', url: robotUrl, showcases: '금속·리벳' },
  { id: 'fluffy-beast', label: '털복숭이', url: beastUrl, showcases: '부드러운 노멀' },
  { id: 'ice-wraith', label: '얼음 망령', url: wraithUrl, showcases: '반투명·서리' },
  { id: 'dragon-whelp', label: '새끼 용', url: dragonUrl, showcases: '비늘' },
];

import sheetKnightUrl from '../assets/sheets/knight.webp';
import sheetGolemUrl from '../assets/sheets/golem.webp';
import sheetDragonUrl from '../assets/sheets/dragon-whelp.webp';
import sheetRobotUrl from '../assets/sheets/robot.webp';
import turnRobot000 from '../assets/turn/robot-000.webp';
import turnRobot090 from '../assets/turn/robot-090.webp';
import turnRobot180 from '../assets/turn/robot-180.webp';
import turnRobot270 from '../assets/turn/robot-270.webp';
import turnKnight000 from '../assets/turn/knight-000.webp';
import turnKnight045 from '../assets/turn/knight-045.webp';
import turnKnight090 from '../assets/turn/knight-090.webp';
import turnKnight135 from '../assets/turn/knight-135.webp';
import turnKnight180 from '../assets/turn/knight-180.webp';
import turnKnight225 from '../assets/turn/knight-225.webp';
import turnKnight270 from '../assets/turn/knight-270.webp';
import turnKnight315 from '../assets/turn/knight-315.webp';
import turnGoblin000 from '../assets/turn/goblin-000.webp';
import turnGoblin045 from '../assets/turn/goblin-045.webp';
import turnGoblin090 from '../assets/turn/goblin-090.webp';
import turnGoblin135 from '../assets/turn/goblin-135.webp';
import turnGoblin180 from '../assets/turn/goblin-180.webp';
import turnGoblin225 from '../assets/turn/goblin-225.webp';
import turnGoblin270 from '../assets/turn/goblin-270.webp';
import turnGoblin315 from '../assets/turn/goblin-315.webp';
import turnGolem000 from '../assets/turn/golem-000.webp';
import turnGolem045 from '../assets/turn/golem-045.webp';
import turnGolem090 from '../assets/turn/golem-090.webp';
import turnGolem135 from '../assets/turn/golem-135.webp';
import turnGolem180 from '../assets/turn/golem-180.webp';
import turnGolem225 from '../assets/turn/golem-225.webp';
import turnGolem270 from '../assets/turn/golem-270.webp';
import turnGolem315 from '../assets/turn/golem-315.webp';
import turnHero000 from '../assets/turn/hero-000.webp';
import turnHero045 from '../assets/turn/hero-045.webp';
import turnHero090 from '../assets/turn/hero-090.webp';
import turnHero135 from '../assets/turn/hero-135.webp';
import turnHero180 from '../assets/turn/hero-180.webp';
import turnHero225 from '../assets/turn/hero-225.webp';
import turnHero270 from '../assets/turn/hero-270.webp';
import turnHero315 from '../assets/turn/hero-315.webp';

/**
 * 3뷰 캐릭터 시트 갤러리. 정면·측면·후면이 한 장에 가로로 놓인 1536×1024.
 * 단일 뷰 갤러리와 같은 캐릭터를 골라 두었다 — 부풀리기와 비주얼 헐을 나란히 비교하라고.
 */
export const SHEET_GALLERY: GalleryItem[] = [
  { id: 'sheet-knight', label: '강철 기사', url: sheetKnightUrl, showcases: '판금 갑옷 · 옆모습' },
  { id: 'sheet-golem', label: '용암 골렘', url: sheetGolemUrl, showcases: '두꺼운 몸통' },
  { id: 'sheet-dragon', label: '새끼 용', url: sheetDragonUrl, showcases: '날개 · 꼬리 (헐의 한계)' },
  { id: 'sheet-robot', label: '로봇', url: sheetRobotUrl, showcases: '각진 실루엣' },
];

/** 턴어라운드 갤러리 — 방위별 뷰 이미지 묶음 (gpt-image-2 low, 정면 참조로 생성). */
export interface TurnGalleryItem {
  id: string;
  label: string;
  showcases: string;
  views: { azimuth: number; url: string }[];
}

export const TURN_GALLERY: TurnGalleryItem[] = [
  {
    id: 'turn-hero', label: '엠버 뱅가드 · 8뷰 (주인공)', showcases: 'chatgpt.com 추론 \'매우 높음\' 한 채팅, 정면 컨셉→8방위 각각 단일 이미지 — 등의 검·이글거리는 갑옷',
    views: [turnHero000, turnHero045, turnHero090, turnHero135, turnHero180, turnHero225, turnHero270, turnHero315]
      .map((url, i) => ({ azimuth: i * 45, url })),
  },
  {
    id: 'turn-goblin', label: '고블린 로그 · 8뷰', showcases: 'GPT Pro 가 한 장 시트(4×2, 칸 313px)로 준 것을 잘라 씀 — 저해상 입력 실측',
    views: [turnGoblin000, turnGoblin045, turnGoblin090, turnGoblin135, turnGoblin180, turnGoblin225, turnGoblin270, turnGoblin315]
      .map((url, i) => ({ azimuth: i * 45, url })),
  },
  {
    id: 'turn-knight', label: '강철 기사 · 8뷰', showcases: 'chatgpt.com 병렬이미지 한 채팅에서 8장 — 망토·판금',
    views: [turnKnight000, turnKnight045, turnKnight090, turnKnight135, turnKnight180, turnKnight225, turnKnight270, turnKnight315]
      .map((url, i) => ({ azimuth: i * 45, url })),
  },
  {
    id: 'turn-golem', label: '이끼 골렘 · 8뷰', showcases: '45° 간격 여덟 장 — 팔각 단면, 후면도',
    views: [turnGolem000, turnGolem045, turnGolem090, turnGolem135, turnGolem180, turnGolem225, turnGolem270, turnGolem315]
      .map((url, i) => ({ azimuth: i * 45, url })),
  },
  {
    id: 'turn-robot', label: '레트로 로봇 · 4뷰', showcases: '90° 간격 넷 — 붉은 판금 (색 충실도 검사용)',
    views: [turnRobot000, turnRobot090, turnRobot180, turnRobot270].map((url, i) => ({ azimuth: i * 90, url })),
  },
];

import headHero000 from '../assets/head/hero-head-000.webp';
import headHero045 from '../assets/head/hero-head-045.webp';
import headHero090 from '../assets/head/hero-head-090.webp';
import headHero135 from '../assets/head/hero-head-135.webp';
import headHero180 from '../assets/head/hero-head-180.webp';
import headHero225 from '../assets/head/hero-head-225.webp';
import headHero270 from '../assets/head/hero-head-270.webp';
import headHero315 from '../assets/head/hero-head-315.webp';
/** 머리 클로즈업 갤러리 — 파츠 합성용. 전신 갤러리와 같은 캐릭터여야 목이 맞는다. */
export const HEAD_GALLERY: TurnGalleryItem[] = [
  {
    id: 'head-hero', label: '엠버 뱅가드 머리', showcases: '얼굴 세로 800px — 전신 8뷰의 120px 과 비교',
    views: [headHero000, headHero045, headHero090, headHero135, headHero180, headHero225, headHero270, headHero315]
      .map((url, i) => ({ azimuth: i * 45, url })),
  },
];

/** 갤러리 이미지를 File로. 드롭한 파일과 완전히 같은 경로를 탄다. */
export async function loadGalleryFile(id: string): Promise<File> {
  const item = GALLERY.find((g) => g.id === id);
  if (!item) throw new Error(`갤러리에 ${id} 가 없습니다.`);
  const res = await fetch(item.url);
  if (!res.ok) throw new Error(`갤러리 이미지를 못 받았습니다 (${res.status}).`);
  const blob = await res.blob();
  return new File([blob], `${item.id}.webp`, { type: blob.type || 'image/webp' });
}

/** 샘플을 PNG File로. 드롭한 파일과 완전히 같은 경로를 타게 된다. */
export async function makeSampleFile(id: string): Promise<File> {
  const spec = SAMPLE_SPECS.find((s) => s.id === id) ?? SAMPLE_SPECS[0]!;
  const canvas = document.createElement('canvas');
  canvas.width = SIZE;
  canvas.height = SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('2D 컨텍스트를 만들 수 없습니다.');
  ctx.putImageData(renderSample(spec), 0, 0);

  const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/png'));
  if (!blob) throw new Error('샘플 이미지를 만들지 못했습니다.');
  return new File([blob], `sample-${spec.id}.png`, { type: 'image/png' });
}
